import React, { useState, useEffect } from "react";
import { Modal, Table, Tag, Empty } from "antd";
import { Loader } from "@fluentui/react-northstar";
import { api } from "../../../../Assets/api/api";
import { useTranslation } from "react-i18next";

const SchoolBranchesDialog = ({
  open,
  onClose,
  schoolId,
  schoolName,
  user,
}) => {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  const MainTranslation = t("trustedNetwork").schools.schoolBranchesDialog;
  const translation = MainTranslation.columnHeaders;
  const emptyState = MainTranslation.emptyState;

  // Detect screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsMediumScreen(width > 768 && width <= 1099);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fetchBranches = async () => {
    if (!schoolId || !open) return;

    setIsLoading(true);
    try {
      const response = await api.get(
        `/api/school/get-branches?slug=${schoolId}`
      );

      if (response.status === 200) {
        const branchesData = response.data?.data || response.data || [];
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && schoolId) {
      fetchBranches();
    }
  }, [open, schoolId]);

  const columns = [
    {
      title: translation.branchName,
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
      width: isMobile ? 120 : undefined,
      fixed: isMobile ? 'left' : undefined,
    },
    {
      title: translation.address,
      dataIndex: "address",
      key: "address",
      render: (text) => text || "-",
      width: isMobile ? 150 : undefined,
      ellipsis: isMobile,
    },
    {
      title: translation.contactPhone,
      dataIndex: "contactPhone",
      key: "contactPhone",
      width: isMobile ? 120 : 100,
      render: (text) => text || "-",
      responsive: isMobile ? ['lg'] : undefined,
    },
    {
      title: translation.email,
      dataIndex: "email",
      key: "email",
      render: (text) => text || "-",
      width: isMobile ? 150 : undefined,
      ellipsis: isMobile,
      responsive: isMobile ? ['md'] : undefined,
    },
    {
      title: translation.type,
      dataIndex: "type",
      key: "type",
      render: (type) => {
        const colors = {
          Public: "blue",
          Private: "green",
          International: "purple",
        };
        return type ? (
          <Tag
            color={colors[type] || "default"}
            style={{ borderRadius: "12px", fontSize: isMobile ? "10px" : "12px" }}
          >
            {type}
          </Tag>
        ) : (
          "-"
        );
      },
      width: isMobile ? 80 : undefined,
    },
    {
      title: translation.category,
      dataIndex: "category",
      key: "category",
      render: (category) => {
        const colors = {
          Urban: "orange",
          Rural: "cyan",
          "Semi-Urban": "geekblue",
          Elite: "gold",
          NGO: "lime",
          Other: "default",
        };
        return category ? (
          <Tag
            color={colors[category] || "default"}
            style={{ borderRadius: "12px", fontSize: isMobile ? "10px" : "12px" }}
          >
            {category}
          </Tag>
        ) : (
          "-"
        );
      },
      width: isMobile ? 90 : undefined,
      responsive: isMobile ? ['lg'] : undefined,
    },
    {
      title: translation.city,
      dataIndex: "city",
      key: "city",
      width: isMobile ? 80 : 100,
      render: (text) => text || "-",
    },
    {
      title: translation.country,
      dataIndex: "country",
      key: "country",
      width: isMobile ? 80 : 100,
      render: (text) => text || "-",
      responsive: isMobile ? ['xl'] : undefined,
    },
  ];

  // Responsive modal styles
  const getModalWidth = () => {
    if (isMobile) return "95vw";
    if (isMediumScreen) return "90vw";
    return 1300;
  };

  const getModalStyles = () => ({
    body: { 
      padding: isMobile ? "10px" : (isMediumScreen ? "15px" : "20px"),
      maxHeight: isMobile ? "70vh" : "80vh",
      overflowY: "auto"
    },
    header: {
      padding: isMobile ? "12px 16px" : "16px 24px"
    }
  });

  return (
    <Modal
      title={
        <div style={{ 
          fontSize: isMobile ? "16px" : "18px", 
          fontWeight: "bold",
          wordBreak: "break-word"
        }}>
          Branches of {schoolName}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={getModalWidth()}
      centered
      styles={getModalStyles()}
    >
      {isLoading ? (
        <div style={{ textAlign: "center", padding: isMobile ? "20px" : "40px" }}>
          <Loader size="medium" />
        </div>
      ) : branches.length > 0 ? (
        <Table
          dataSource={branches}
          columns={columns}
          rowKey="id"
          pagination={isMobile ? { pageSize: 5, size: "small" } : false}
          scroll={{ 
            x: isMobile ? 800 : (isMediumScreen ? 1000 : 1200),
            y: isMobile ? 300 : undefined
          }}
          size={isMobile ? "small" : "middle"}
          style={{
            fontSize: isMobile ? "12px" : "14px"
          }}
        />
      ) : (
        <Empty
          description={
            <div>
              <div style={{ 
                fontSize: isMobile ? "14px" : "16px", 
                marginBottom: "8px" 
              }}>
                {emptyState.noBranchesFound}
              </div>
              <div style={{ 
                color: "#666", 
                fontSize: isMobile ? "12px" : "14px" 
              }}>
                {emptyState.noBranchesDescription}
              </div>
            </div>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Modal>
  );
};

export default SchoolBranchesDialog;
